/**
 * Module 58 (WP-58.9) — keys the PLATFORM owns and shared UI must not bind.
 *
 * CrazyGames reserves Escape for exiting fullscreen; other portals reserve
 * their own. Shared components cannot take a host object (they render on the
 * main site too, where no host exists), so this is a boot-time platform
 * signal in the exact mould of `initLayoutMode`: the composition root states
 * the fact once before first render, and components ask a pure question.
 *
 * ## Why this is not the "global singleton" rule 2 bans
 *
 * Rule 2 bans singletons that smuggle DEPENDENCIES into game or framework
 * code. This module carries a platform FACT, set once at boot by the only
 * layer that knows it (the composition root, from `IEmbedHost.reservedKeys`),
 * and read as a constant thereafter — the same shape as the `data-layout`
 * signal and `initLayoutMode`. A component receiving `reservedKeys` as a prop
 * through five layers of games that must not know hosts exist would be the
 * seam in the wrong place.
 *
 * ## Default: nothing reserved
 *
 * A root that never calls {@link setReservedKeys} gets every binding, which is
 * exactly today's behaviour on every platform (rule 9) — the CrazyGames
 * adapter deliberately declares `[]` until Module 59 makes Escape's
 * reservation a verified behaviour change of its own.
 */
let reserved = new Set();
/** State the platform's reservations. Called once, at boot, by a composition root. */
export function setReservedKeys(keys) {
    reserved = new Set(keys);
}
/** May shared UI bind this key? Components check before attaching a handler. */
export function isReservedKey(key) {
    return reserved.has(key);
}
/** Test seam — a boot-time fact needs resetting between tests, like the layout signal. */
export function resetReservedKeys() {
    reserved = new Set();
}
